import { AfterViewInit, Directive, ElementRef, Input } from '@angular/core';
import { FocusService } from './focus.service';

/**
 * Automatically or manually sets focus on an element using the {@link FocusService}.
 *
 * @remarks
 * This directive provides a declarative way to manage element focus in templates.
 * When applied to an element with a truthy value, focus is automatically set after
 * the view initializes. The directive can also be used with an empty string attribute
 * to enable focus immediately. For programmatic control beyond initialization,
 * call the `focus()` method of the directive.
 *
 * The directive works with any focusable element (inputs, buttons, textareas, etc.)
 * and automatically validates focusability before applying focus.
 *
 * @example
 *
 * ```html
 * <input
 *   type="text"
 *   placeholder="Username"
 *   [bbFocus]="isLoginForm"
 * />
 * ```
 */
@Directive({
  selector: '[bbFocus]',
  exportAs: 'bbFocus',
  standalone: false,
})
export class FocusDirective implements AfterViewInit {
  /**
   * Controls whether the element receives focus after the view initializes.
   *
   * @remarks
   * When set to `true` or an empty string (`bbFocus=""` or `[bbFocus]="true"`),
   * the directive automatically focuses the element. When set to `false` or any
   * other falsy value, focus is not applied. This property is useful for
   * conditionally enabling autofocus based on application state.
   *
   * @example
   *
   * ```html
   * <!-- Conditionally focus based on state -->
   * <input
   *   type="password"
   *   placeholder="Enter password"
   *   [bbFocus]="isPasswordStep"
   * />
   *
   * <!-- Always focus immediately (empty string shorthand) -->
   * <button bbFocus>Submit Form</button>
   * ```
   */
  @Input()
  set bbFocus(value: boolean | '') {
    this.autofocus = value === '' || !!value;
  }

  private autofocus = true;

  constructor(
    private readonly service: FocusService,
    private readonly el: ElementRef,
  ) {}

  /**
   * Applies focus to the element after the view has been initialized.
   *
   * @remarks
   * This lifecycle method is invoked automatically by Angular after the component
   * view is initialized. If the `bbFocus` input was set to a truthy value or an
   * empty string, focus is applied to the host element through the {@link FocusService}.
   */
  ngAfterViewInit(): void {
    if (this.autofocus) {
      this.focus();
    }
  }

  /**
   * Manually triggers focus on the element.
   *
   * @remarks
   * This method can be called programmatically to focus the element at any time,
   * bypassing the `bbFocus` input state. It delegates to the {@link FocusService}
   * which handles throttling and focusability validation.
   */
  focus() {
    this.service.focus(this.el);
  }
}
